// Écrans affichés avant la connexion : premier lancement (création de l'administrateur) et connexion.
import { useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError, unwrap } from "../api/client";
import type { Me } from "../api/types";
import { t } from "../lib/i18n";
import { ContactButton, MailLink, useContacts } from "./Contacts";
import { LangSwitch } from "./LangSwitch";
import { CodeInput, TotpEnroll } from "./Totp";
import { Button, Field, FormError, TextInput } from "./ui";

export function Card({ title, intro, children, wide = false }: { title: string; intro: ReactNode; children: ReactNode; wide?: boolean }) {
  return (
    <div className="grid min-h-screen place-items-center p-4">
      <div className={`flex w-full ${wide ? "max-w-lg" : "max-w-sm"} flex-col gap-5 rounded-lg border border-line bg-surface p-6`}>
        <div className="flex items-center gap-2.5">
          <span className="grid size-7 place-items-center rounded bg-raised">
            <span className="size-3 rounded-full bg-ok shadow-[0_0_8px_var(--color-ok)]" />
          </span>
          <span className="font-cond text-lg font-semibold tracking-wide">Gateway Live</span>
          <LangSwitch className="ml-auto" />
        </div>
        <div>
          <h1 className="font-cond text-xl font-semibold tracking-wide">{title}</h1>
          <div className="mt-1 text-muted">{intro}</div>
        </div>
        {children}
        <ContactButton />
      </div>
    </div>
  );
}

export function LoginScreen({ onLoggedIn, signupEnabled = false }: { onLoggedIn: (me: Me) => void; signupEnabled?: boolean }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // null tant que le serveur n'a pas demandé le code (HTTP 428 : compte avec double authentification)
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = { username: username.trim(), password, code: code === null ? null : code.replace(/\s/g, "") };
      onLoggedIn(await unwrap(api.POST("/api/auth/login", { body })));
    } catch (err) {
      if (err instanceof ApiError && err.status === 428) {
        setCode("");
      } else if (code !== null && err instanceof ApiError && err.status === 401) {
        setError(err.message);
        setCode("");
      } else {
        setError((err as Error).message);
        setPassword("");
        setCode(null);
      }
    } finally {
      setBusy(false);
    }
  };

  const support = useContacts()?.support_email;
  return (
    <Card title={t("Connexion")} intro={t("Connectez-vous avec le compte fourni par l'administrateur de la gateway.")}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t("Identifiant")} htmlFor="login-user">
          <TextInput id="login-user" autoComplete="username" autoFocus required value={username}
            onChange={(e) => setUsername(e.target.value)} />
        </Field>
        <Field label={t("Mot de passe")} htmlFor="login-pass">
          <TextInput id="login-pass" type="password" autoComplete="current-password" required value={password}
            onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {code !== null && (
          <Field label={t("Code de l'application d'authentification")} htmlFor="login-code"
            hint={t("Le code à 6 chiffres affiché sur votre téléphone.")}>
            <CodeInput id="login-code" autoFocus value={code} onChange={setCode} />
          </Field>
        )}
        <FormError message={error} />
        <Button variant="primary" type="submit" disabled={busy}>{t("Se connecter")}</Button>
      </form>
      {signupEnabled && (
        <p className="border-t border-line pt-4 text-center text-[13px] text-muted">
          {t("Pas encore de compte ?")} <a className="text-accent hover:underline" href="#/inscription">{t("Demander un accès")}</a>
        </p>
      )}
      {support && (
        <p className="text-center text-[13px] text-muted">{t("Contact support :")} <MailLink address={support} /></p>
      )}
    </Card>
  );
}

export function SetupScreen({ allowed, onDone }: { allowed: boolean; onDone: (me: Me) => void }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!allowed) {
    return (
      <Card title={t("Première installation")} intro={t("Aucun compte n'existe encore sur cette gateway.")}>
        <p className="text-muted">
          {t("Pour des raisons de sécurité, le premier compte administrateur se crée depuis le serveur lui-même : ouvrez cette page sur le serveur (http://127.0.0.1:8000), ou lancez sur le serveur :")}
        </p>
        <pre className="overflow-x-auto rounded-md border border-line bg-bg p-3 font-mono text-[12px] text-ink">
          cd /opt/gateway/backend{"\n"}sudo -u gateway .venv/bin/python -m app create-admin admin
        </pre>
        <Button onClick={() => window.location.reload()}>{t("J'ai créé le compte")}</Button>
      </Card>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError(t("Les deux mots de passe diffèrent."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onDone(await unwrap(api.POST("/api/auth/setup", { body: { username: username.trim(), password } })));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t("Première installation")}
      intro={t("Créez le compte administrateur. Vous pourrez ensuite créer les comptes des opérateurs.")}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t("Identifiant")} htmlFor="setup-user">
          <TextInput id="setup-user" autoComplete="username" required value={username}
            onChange={(e) => setUsername(e.target.value)} />
        </Field>
        <Field label={t("Mot de passe")} htmlFor="setup-pass" hint={t("10 caractères minimum. Une phrase de passe est idéale.")}>
          <TextInput id="setup-pass" type="password" autoComplete="new-password" autoFocus required minLength={10}
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Field label={t("Confirmer")} htmlFor="setup-confirm">
          <TextInput id="setup-confirm" type="password" autoComplete="new-password" required value={confirm}
            onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <FormError message={error} />
        <Button variant="primary" type="submit" disabled={busy}>{t("Créer le compte et continuer")}</Button>
      </form>
    </Card>
  );
}

/** Administrateur sans double authentification : rien d'autre n'est accessible avant de l'activer. */
export function TotpRequiredScreen({ me, onEnabled, onLogout }: {
  me: Me; onEnabled: (me: Me) => void; onLogout: () => void;
}) {
  return (
    <Card title={t("Double authentification")}
      intro={<>{t("Obligatoire pour les administrateurs. Compte :")} <span className="text-ink">{me.username}</span></>}>
      <TotpEnroll formId="totp-required" onEnabled={onEnabled} />
      <Button variant="ghost" onClick={onLogout}>{t("Se déconnecter")}</Button>
    </Card>
  );
}
